import { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { authSession, useAuth } from "@/features/auth";
import { usePwaUpdate } from "./UpdateContext";
import { UpdateControls } from "./UpdateControls";
import { Button } from "@/shared/components/ui/button";

/** Preserve the routed page underneath a modal gate, including unsaved form state. */
export function RequiredUpdateScreen() {
  const update = usePwaUpdate();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const dialog = useRef<HTMLDialogElement>(null);
  const show = update.required && location.pathname !== "/about";
  useEffect(() => {
    const element = dialog.current;
    if (show && element && !element.open) element.showModal();
    else if (!show && element?.open) element.close();
  }, [show]);
  return (
    <dialog
      ref={dialog}
      onCancel={(event) => event.preventDefault()}
      aria-labelledby="required-update-title"
      dir="rtl"
      className="m-auto w-[calc(100%-2rem)] max-w-md space-y-4 rounded-2xl p-6 backdrop:bg-slate-950/60"
    >
      <h1 id="required-update-title" className="text-lg font-bold">
        برای ادامه برنامه را به‌روز کنید
      </h1>
      <p className="text-sm leading-7">
        این نسخه دیگر پشتیبانی نمی‌شود. صفحه فعلی شما تا زمان بارگذاری مجدد حفظ
        می‌شود.
      </p>
      <UpdateControls />
      <Link
        to="/about"
        state={{ fromUpdateGate: true }}
        className="text-brand block text-sm underline"
      >
        درباره برنامه و تغییرات نسخه
      </Link>
      {isAuthenticated && (
        <Button
          variant="outlineBrand"
          onClick={() => {
            authSession.clearSession();
            void navigate("/", { replace: true });
          }}
        >
          خروج از حساب
        </Button>
      )}
    </dialog>
  );
}

/** New backend notes can be announced without requiring a frontend update or reload. */
export function WhatsNewNotice() {
  const { announcements, dismissAnnouncements, required } = usePwaUpdate();
  const location = useLocation();
  const first = announcements[0];
  if (!first || required || location.pathname === "/about") return null;
  return (
    <aside
      role="status"
      dir="rtl"
      className="fixed inset-x-4 top-4 z-40 mx-auto max-w-md space-y-2 rounded-2xl border border-sky-100 bg-white p-4 shadow-lg"
    >
      <h2 className="text-sm font-bold">تازه‌های کایر</h2>
      <p className="text-xs leading-6">
        {first.release.notes.find((n) => n.userFacing)?.text}
      </p>
      <div className="flex gap-3 text-xs">
        <Link
          to="/about"
          onClick={dismissAnnouncements}
          className="text-brand underline"
        >
          مشاهده تغییرات
        </Link>
        <button type="button" onClick={dismissAnnouncements}>
          بعداً
        </button>
      </div>
    </aside>
  );
}
